import React, { useMemo, useRef, useEffect } from 'react';
import {
  OnboardingButton,
  OnboardingItemContainer,
  OnboardingItemContent,
  OnboardingItemDescription,
  OnboardingItemImage,
  OnboardingItemTitle,
  OnboardingItemTitleAndDescription,
  OnboardingItemVideo,
  RowOnboarding,
} from 'modules/onboarding/components';
import useContentfulRenderer from 'hooks/useContentfulRenderer';
import { Onboarding } from 'models/Onboarding';
import localizedStrings from 'localization';
import theme from 'utils/theme';
import {
  getAppOnboardings,
  getAppOnboardingSeen,
  getQuestOffboardingSeen,
  getQuestOnboardingSeen,
  getWeekOnboardingSeen,
  notifyQuestOffboardingSeen
} from '../actions';
import { useDispatch, useSelector } from 'react-redux';
import { selectContactActiveCampaignId, selectIsNewContactActiveCampaign } from '../selectors';
import { selectActiveQuest } from 'modules/loading-screen/quests/selectors';
import { useHistory } from 'react-router';
import { HOME, ONBOARDING } from 'routes';
import { CONTACT_OPTIONS } from '../onboarding-constants';
import { DoneButton } from 'models/DoneButton';
import useActionStatus from 'hooks/useActionStatus';
import { getSchedules } from 'modules/loading-screen/schedules/actions';
import { getScheduleCommit } from 'modules/loading-screen/schedule-commit/actions';
import { getQuests } from 'modules/loading-screen/contentful/quests/actions';
import { getQuestsOnboardings } from 'modules/loading-screen/contentful/quests-onboardings/actions';
import { getQuestsOffboardings } from 'modules/loading-screen/contentful/quests-offboardings/actions';
import { getWeeks } from 'modules/loading-screen/contentful/weeks/actions';
import { getWeeksOnboardings } from 'modules/loading-screen/contentful/weeks-onboardings/actions';
import { getContents } from 'modules/loading-screen/contentful/contents/actions';
import { getQuestApplets } from 'modules/applets/actions';
import { ONBOARDING_WEEK } from '../routes';

type SharedOnboardingProps = {
  item: Onboarding;
  lastItem: boolean;
  activeIndex: number;
  handleGetStarted: () => void;
};

const OnboardingItem = ({
  item,
  lastItem,
  activeIndex,
  handleGetStarted,
}: SharedOnboardingProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const vidRef = useRef<HTMLVideoElement>(null);
  const isAutoPlay = item.number - 1 === activeIndex;
  const isPaused = item.number - 1 !== activeIndex;

  const buttonMain = item.button
  const buttonAction = item?.buttonAction
  const buttonActionAdditional = item?.buttonActionAdditional

  const newContactActiveCampaign = useSelector(selectIsNewContactActiveCampaign)
  const contactId = useSelector(selectContactActiveCampaignId)
  const activeQuest = useSelector(selectActiveQuest);

  const [, getAppOnboardingsFulfilled] = useActionStatus(getAppOnboardings);
  const [, getAppOnboardingSeenFulfilled] = useActionStatus(getAppOnboardingSeen);
  const [, questOnboardingSeenWasFulfilled] = useActionStatus(getQuestOnboardingSeen);
  const [, questOffboardingSeenWasFulfilled] = useActionStatus(getQuestOffboardingSeen);
  const [, getWeekOnboardingSeenFulfilled] = useActionStatus(getWeekOnboardingSeen);
  const [, getSchedulesWasFulfilled] = useActionStatus(getSchedules);
  const [, scheduleCommitWasFulfilled] = useActionStatus(getScheduleCommit);
  const [, getQuestsWasFulfilled] = useActionStatus(getQuests);
  const [, getQuestsOnboardingsWasFulfilled] = useActionStatus(getQuestsOnboardings);
  const [, getQuestsOffboardingsWasFulfilled] = useActionStatus(getQuestsOffboardings);
  const [, getWeeksWasFulfilled] = useActionStatus(getWeeks);
  const [, getWeeksOnboardingsWasFulfilled] = useActionStatus(getWeeksOnboardings);
  const [, getContentsWasFulfilled] = useActionStatus(getContents);
  const [, getAppletsWasFulfilled] = useActionStatus(getQuestApplets);

  const isLocationOnboardingWeek = history.location.pathname === ONBOARDING + ONBOARDING_WEEK;
  const isFetching = useMemo(() =>
    !(
      getAppOnboardingsFulfilled &&
      getAppOnboardingSeenFulfilled &&
      questOnboardingSeenWasFulfilled &&
      questOffboardingSeenWasFulfilled &&
      getWeekOnboardingSeenFulfilled &&
      scheduleCommitWasFulfilled &&
      getQuestsWasFulfilled &&
      getQuestsOnboardingsWasFulfilled &&
      getQuestsOffboardingsWasFulfilled &&
      getWeeksWasFulfilled &&
      getWeeksOnboardingsWasFulfilled &&
      getContentsWasFulfilled &&
      getAppletsWasFulfilled &&
      getSchedulesWasFulfilled
    )
    , [
      getAppOnboardingsFulfilled,
      getAppOnboardingSeenFulfilled,
      questOnboardingSeenWasFulfilled,
      questOffboardingSeenWasFulfilled,
      getWeekOnboardingSeenFulfilled,
      scheduleCommitWasFulfilled,
      getQuestsWasFulfilled,
      getQuestsOnboardingsWasFulfilled,
      getQuestsOffboardingsWasFulfilled,
      getWeeksWasFulfilled,
      getWeeksOnboardingsWasFulfilled,
      getContentsWasFulfilled,
      getAppletsWasFulfilled,
      getSchedulesWasFulfilled,
    ]);

  useEffect(() => {
    if (isAutoPlay && vidRef.current) {
      vidRef.current.play();
    } else if (isPaused && vidRef.current) {
      vidRef.current.pause();
    }
  }, [isAutoPlay, isPaused, vidRef]);

  const description = useContentfulRenderer(item.description, {
    darkMode: true,
  });

  const image = useMemo(
    () => (item.imageWeb ? `https:${item.imageWeb.fields.file.url}` : ''),
    [item],
  );

  const video = useMemo(
    () => (item.videoWeb ? `https:${item.videoWeb.fields.file.url}` : ''),
    [item],
  );

  const handleButtonAction = (btnItem: DoneButton) => {
    const contactExists = newContactActiveCampaign === CONTACT_OPTIONS.CONTACT_EXISTS;
    const buttonActions = btnItem?.action;

    if (contactExists && contactId && buttonActions && Boolean(buttonActions?.length)) {
      buttonActions.forEach(tag => {
        const tagNumber = parseInt(tag.split('-')[1]);
        if (!isNaN(tagNumber)) {
          // Service with Payment Required. Active Campaign Disabled
          // dispatch(addContactActiveCampaign({ contact: contactId, tag: tagNumber.toString() }))
        }
      });
    }
    if (activeQuest) {
      dispatch(notifyQuestOffboardingSeen({ questId: activeQuest.id }));
    }
    history.push(HOME);
  }

  const handleButtonLast = () => {
    if (buttonMain?.action) {
      handleButtonAction(buttonMain)
    } else {
      handleGetStarted()
    }
  }

  // Service with Payment Required. Active Campaign Disabled
  // useEffect(() => {
  //   if (email) {
  //     dispatch(getContactActiveCampaignByEmail(encodeURIComponent(email)))
  //   }
  //   return () => { }
  // }, [email, dispatch])

  // Service with Payment Required. Active Campaign Disabled
  // useEffect(() => {
  //   const canContactActiveCampaign = newContactActiveCampaign === CONTACT_OPTIONS.IS_NEW
  //   if (canContactActiveCampaign && email && firstName && lastName) {
  //     dispatch(
  //       createContactActiveCampaign({
  //         email,
  //         firstName,
  //         lastName,
  //         phone: '-',
  //       }),
  //     );
  //   }
  //   return () => { }
  // }, [email, firstName, lastName, newContactActiveCampaign, dispatch])

  return (
    <OnboardingItemContainer>
      {video ? (
        <OnboardingItemVideo
          ref={vidRef}
          src={video}
          autoPlay={isAutoPlay}
          controls
        />
      ) : (
        <OnboardingItemImage backgroundImage={image} />
      )}
      <OnboardingItemContent>
        <OnboardingItemTitleAndDescription>
          <OnboardingItemTitle>{item.title}</OnboardingItemTitle>
          <OnboardingItemDescription>{description}</OnboardingItemDescription>
        </OnboardingItemTitleAndDescription>

        <RowOnboarding>

          {buttonAction ?
            <OnboardingButton
              onClick={() => handleButtonAction(buttonAction)}
              backgroundColor={buttonAction?.color || theme.colors.green[90]}>
              {buttonAction?.text || localizedStrings.doNextChallenge}
            </OnboardingButton> : null}

          {buttonActionAdditional ?
            <OnboardingButton
              onClick={() => handleButtonAction(buttonActionAdditional)}
              backgroundColor={buttonActionAdditional?.color || theme.colors.green[90]}>
              {buttonActionAdditional?.text || localizedStrings.done}
            </OnboardingButton> : null}

          {(lastItem) && (
            <OnboardingButton
              isLoading={isLocationOnboardingWeek ? isFetching : false}
              onClick={handleButtonLast}
              backgroundColor={buttonMain?.color}>
              {buttonMain?.text || localizedStrings.getStarted}
            </OnboardingButton>
          )}

        </RowOnboarding>

      </OnboardingItemContent>
    </OnboardingItemContainer>
  );
};

export default OnboardingItem;
